import { IDetailFetcher } from '../fetchers/IDetailFetcher';
import { IListFetcher } from '../fetchers/IListFetcher';
import { RequestHelper } from '../helpers/RequestHelper';
import { ResultHelper } from '../helpers/ResultHelper';
import { Sanitizer } from '../helpers/Sanitizer';
import { ServerApiMapper } from '../helpers/ServerModelHelper';
import { ProjectMapper } from '../mappers/ProjectMapper';
import { IProjectDetailInfo, IProjectInfo } from '../models/IProjectInfo';
import { INamedItemInfo } from '../models/resourceInfoParts';
import { Module } from '../Module';
import { CreateProjectRequest, IProjectIdResult } from '../ServerApi/requests/CreateProjectRequest';
import { DeleteProjectsRequest } from '../ServerApi/requests/DeleteProjectsRequest';
import { GetFieldValuesRequest } from '../ServerApi/requests/GetFieldValuesRequest';
import { GetProjectActionsRequest } from '../ServerApi/requests/GetProjectActionsRequest';
import { GetProjectAncestorsRequest, IGetProjectAncestorsResult } from '../ServerApi/requests/GetProjectAncestorsRequest';
import { GetProjectsRequest } from '../ServerApi/requests/GetProjectsRequest';
import { MoveProjectsToProjectRequest } from '../ServerApi/requests/MoveProjectsToProjectRequest';
import { ISetContentControlledPermissionsParams, SetContentControlledPermissionsRequest } from '../ServerApi/requests/SetContentControlledPermissionsRequest';
import { ISetProjectControlledPermissionsParams, SetProjectControlledPermissionsRequest } from '../ServerApi/requests/SetProjectControlledPermissionsRequest';
import { SetProjectDescriptionRequest } from '../ServerApi/requests/SetProjectDescriptionRequest';
import { SetProjectNameRequest } from '../ServerApi/requests/SetProjectNameRequest';
import { SetProjectsOwnerRequest } from '../ServerApi/requests/SetProjectsOwnerRequest';
import { IActionResult } from '../ServerApi/types/IActionResult';
import { IFieldValue } from '../ServerApi/types/IFieldValue';
import { FieldValueResourceType } from '../ServerApi/types/IFieldValuesParams';
import { IProjectActions } from '../ServerApi/types/IProjectActions';
import { IEmptyResult, IResult } from '../ServerApi/types/IResponse';
import { ErrorNotifyingServer } from '../services/ErrorNotifyingServer';
import { FetcherFactory } from '../services/FetcherFactory';
import { ServerService } from '../services/ServerService';
import { FieldValuesQuery } from './FieldValuesQuery';
import { IActionResource, IDetailResource, IFieldValuesResource, IListResource } from './Resource';
import { ResourceQuery } from './ResourceQuery';

export class Projects implements IListResource<IProjectInfo>, IFieldValuesResource, IDetailResource<IProjectDetailInfo>, IActionResource<IProjectActions> {

  public static $inject = [
    'ErrorNotifyingServer',
    'FetcherFactory'
  ];

  private server: ServerService;
  private listFetcher: IListFetcher<IProjectInfo>;
  private allQuery: ResourceQuery<IProjectInfo>;
  private detailFetcher: IDetailFetcher<IProjectDetailInfo>;
  private fieldValuesFetcher: IListFetcher<IFieldValue>;

  constructor(
    private ErrorNotifyingServer: ErrorNotifyingServer,
    FetcherFactory: FetcherFactory
  ) {
    this.server = ErrorNotifyingServer.getServerService();
    this.listFetcher = FetcherFactory.getServerResourceFetcher(GetProjectsRequest, ServerApiMapper.projects);
    this.allQuery = new ResourceQuery(this.listFetcher);
    this.detailFetcher = FetcherFactory.getServerDetailFetcher('getProject', ProjectMapper.mapDetail);
    this.fieldValuesFetcher = FetcherFactory.getServerResourceFetcher(GetFieldValuesRequest, ServerApiMapper.fieldValues);
  }

  public all(): ResourceQuery<IProjectInfo> {
    return this.allQuery;
  }

  public getById(id: string): ng.IPromise<IProjectDetailInfo> {
    return this.detailFetcher.fetchById(id);
  }

  public valuesForField(field: string): FieldValuesQuery {
    return new FieldValuesQuery(this.fieldValuesFetcher, field, 'project');
  }

  // Actions

  public actions(id: string): ng.IPromise<IProjectActions> {
    return this.server.sendRequest(new GetProjectActionsRequest(id));
  }

  public del(ids: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new DeleteProjectsRequest(idsBlock)),
      ResultHelper.combineResults);
  }

  public rename(id: string, name: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestAndDelay(new SetProjectNameRequest(id, name));
  }

  public setDescription(id: string, description: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequest(new SetProjectDescriptionRequest(id, description));
  }

  public setProjectControlledPermissions(params: ISetProjectControlledPermissionsParams): ng.IPromise<IEmptyResult> {
    return this.ErrorNotifyingServer.sendRequest(
      new SetProjectControlledPermissionsRequest(params));
  }

  public setContentControlledPermissions(params: ISetContentControlledPermissionsParams): ng.IPromise<IActionResult> {
    return this.ErrorNotifyingServer.sendRequest(
      new SetContentControlledPermissionsRequest(params));
  }

  public setOwner(ids: string[], ownerId: string): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(ids, (idsBlock) => new SetProjectsOwnerRequest(idsBlock, ownerId)),
      ResultHelper.combineResults);
  }

  /**
   * Returns all the ancestors in the nested project chain from the top level,
   * including the project with the given ID.
   */
  public getProjectHierarchy(id: string): ng.IPromise<INamedItemInfo[]> {
    return this.ErrorNotifyingServer.sendRequest(new GetProjectAncestorsRequest(id))
        .then((result: IGetProjectAncestorsResult) => result.projects ? result.projects : [])
        .catch(ex => []);
  }

  public moveToProject(targetProjectId: string, projectIds: string[]): ng.IPromise<IResult> {
    return this.ErrorNotifyingServer.sendRequestsAndDelay(
      RequestHelper.buildRequests(projectIds,
        (idsBlock) => new MoveProjectsToProjectRequest({
          ids: idsBlock,
          projectId: targetProjectId
        })),
      ResultHelper.combineResults);
  }

  public create(name: string, description?: string, parentProjectId?: string): ng.IPromise<IProjectIdResult> {
    description = Sanitizer.trimTextOrUndefined(description);

    return this.ErrorNotifyingServer.sendRequestAndDelay(new CreateProjectRequest({
      name: name,
      description: description,
      parentProjectId: parentProjectId
    }));
  }

}

Module.instance().service('Projects', Projects);
